import { useMemo, useState } from "react";
import SectionHeading from "@/pages/home/components/SectionHeading";
import FilmCard from "@/pages/home/components/FilmCard";
import {
  featuredFilms,
  actionFilms,
  scifiFilms,
  tvSeries,
  classicFilms,
  filterTabs,
} from "@/mocks/home";

interface PoolItem {
  id: string;
  title: string;
  original?: string;
  poster: string;
  rating: string;
  meta: string;
  duration?: string;
  summary: string;
  tags: string[];
  type: string;
}

function buildPool(): PoolItem[] {
  const featured: PoolItem[] = featuredFilms.map((f) => ({
    id: f.id,
    title: f.title,
    original: f.original,
    poster: f.poster,
    rating: f.rating,
    meta: f.region,
    duration: f.duration,
    summary: f.summary,
    tags: f.tags,
    type: f.type,
  }));
  const action: PoolItem[] = actionFilms.map((f) => ({
    id: f.id,
    title: f.title,
    poster: f.poster,
    rating: f.rating,
    meta: `${f.region} · ${f.meta}`,
    summary: f.summary,
    tags: ["动作片", f.meta],
    type: "动作片",
  }));
  const scifi: PoolItem[] = scifiFilms.map((f) => ({
    id: f.id,
    title: f.title,
    poster: f.poster,
    rating: f.rating,
    meta: `${f.region} · ${f.meta}`,
    summary: f.summary,
    tags: ["科幻片", f.meta],
    type: "科幻片",
  }));
  const tv: PoolItem[] = tvSeries.map((f) => ({
    id: f.id,
    title: f.title,
    poster: f.poster,
    rating: f.rating,
    meta: `${f.region} · ${f.episodes}`,
    summary: f.summary,
    tags: ["电视剧", f.episodes],
    type: "电视剧",
  }));
  const classic: PoolItem[] = classicFilms.map((f) => ({
    id: f.id,
    title: f.title,
    poster: f.poster,
    rating: f.rating,
    meta: `${f.region} · ${f.era}`,
    summary: f.summary,
    tags: ["经典老片", f.era],
    type: "经典老片",
  }));
  return [...featured, ...action, ...scifi, ...tv, ...classic];
}

export default function FeaturedSection() {
  const pool = useMemo(buildPool, []);
  const [activeTab, setActiveTab] = useState("all");
  const [keyword, setKeyword] = useState("");
  const [visible, setVisible] = useState(8);

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return pool.filter((item) => {
      const matchTab = activeTab === "all" || item.type === activeTab;
      const matchKw =
        !kw ||
        item.title.toLowerCase().includes(kw) ||
        (item.original ? item.original.toLowerCase().includes(kw) : false) ||
        item.summary.toLowerCase().includes(kw) ||
        item.tags.some((t) => t.toLowerCase().includes(kw));
      return matchTab && matchKw;
    });
  }, [pool, activeTab, keyword]);

  const shown = filtered.slice(0, visible);

  return (
    <section id="featured" className="w-full py-14 md:py-20 bg-background-100">
      <div className="w-full px-4 md:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <SectionHeading
            eyebrow="热门推荐"
            title="近期值得一看的影视片单"
            desc="按类型挑片更省事。下面这份片单覆盖动作、科幻、电视剧和经典老片，可以直接用搜索框或类型标签筛选本页已有条目。"
          />
          <div className="w-full lg:w-80 shrink-0">
            <label htmlFor="film-search" className="sr-only">
              搜索本页片单
            </label>
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
              <input
                id="film-search"
                type="text"
                value={keyword}
                onChange={(e) => {
                  setKeyword(e.target.value);
                  setVisible(8);
                }}
                placeholder="搜索片名、类型或关键词"
                className="w-full h-11 pl-9 pr-3 rounded-md border border-background-300/70 bg-background-50 text-sm font-label text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30"
              />
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {filterTabs.map((tab) => {
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => {
                  setActiveTab(tab.key);
                  setVisible(8);
                }}
                className={`h-9 px-4 rounded-full text-sm font-label font-medium transition-colors cursor-pointer whitespace-nowrap border ${
                  active
                    ? "bg-primary-500 text-background-50 border-primary-500"
                    : "bg-background-50 text-foreground-700 border-background-300/70 hover:border-primary-300 hover:text-primary-700"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {shown.length > 0 ? (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
            {shown.map((item) => (
              <FilmCard
                key={item.id}
                title={item.title}
                original={item.original}
                poster={item.poster}
                rating={item.rating}
                meta={item.meta}
                duration={item.duration}
                summary={item.summary}
                tags={item.tags}
                type={item.type}
              />
            ))}
          </div>
        ) : (
          <div className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-50 py-14 text-center">
            <span className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-emotion-sad-line text-2xl" />
            </span>
            <p className="mt-3 text-sm text-foreground-600 font-label">
              没有找到匹配「{keyword}」的条目，换个关键词或选择其他类型试试。
            </p>
          </div>
        )}

        {filtered.length > visible ? (
          <div className="mt-8 flex justify-center">
            <button
              type="button"
              onClick={() => setVisible((v) => v + 8)}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-50 border border-background-300/70 text-sm font-label font-medium text-foreground-800 hover:border-primary-400 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              加载更多片目
              <i className="ri-arrow-down-line" />
            </button>
          </div>
        ) : (
          <p className="mt-8 text-center text-xs text-foreground-400 font-label">
            已展示当前筛选下的全部 {filtered.length} 条片目
          </p>
        )}
      </div>
    </section>
  );
}